/**
 * Built-in functions and keywords for DSL autocomplete
 */
// Built-in functions
export const BUILTIN_FUNCTIONS = [
    {
        name: 'sma',
        description: 'Simple Moving Average',
        params: [
            { name: 'series', type: 'series<float>', description: 'Price series (e.g., close)' },
            { name: 'period', type: 'int', description: 'Period length' },
        ],
        returnType: 'series<float>',
        example: 'sma(close, 20)',
    },
    {
        name: 'ema',
        description: 'Exponential Moving Average',
        params: [
            { name: 'series', type: 'series<float>', description: 'Price series' },
            { name: 'period', type: 'int', description: 'Period length' },
        ],
        returnType: 'series<float>',
        example: 'ema(close, 12)',
    },
    {
        name: 'atr',
        description: 'Average True Range',
        params: [{ name: 'period', type: 'int', description: 'Period length' }],
        returnType: 'series<float>',
        example: 'atr(14)',
    },
    {
        name: 'vwap',
        description: 'Volume Weighted Average Price',
        params: [],
        returnType: 'series<float>',
        example: 'vwap()',
    },
    {
        name: 'crossover',
        description: 'Detect when series1 crosses above series2',
        params: [
            { name: 'series1', type: 'series<float>' },
            { name: 'series2', type: 'series<float>' },
        ],
        returnType: 'series<bool>',
        example: 'crossover(ma_fast, ma_slow)',
    },
    {
        name: 'crossunder',
        description: 'Detect when series1 crosses below series2',
        params: [
            { name: 'series1', type: 'series<float>' },
            { name: 'series2', type: 'series<float>' },
        ],
        returnType: 'series<bool>',
        example: 'crossunder(ma_fast, ma_slow)',
    },
    {
        name: 'pivothigh',
        description: 'Swing high: source higher than leftBars bars left AND rightBars bars right. Returns source value or na.',
        params: [
            { name: 'source', type: 'series<float>' },
            { name: 'leftBars', type: 'int' },
            { name: 'rightBars', type: 'int' },
        ],
        returnType: 'series<float>',
        example: 'pivothigh(high, 2, 2)',
    },
    {
        name: 'pivotlow',
        description: 'Swing low: source lower than leftBars bars left AND rightBars bars right. Returns source value or na.',
        params: [
            { name: 'source', type: 'series<float>' },
            { name: 'leftBars', type: 'int' },
            { name: 'rightBars', type: 'int' },
        ],
        returnType: 'series<float>',
        example: 'pivotlow(low, 2, 2)',
    },
    {
        name: 'abs',
        description: 'Absolute value',
        params: [{ name: 'value', type: 'series<float>' }],
        returnType: 'series<float>',
        example: 'abs(close - open)',
    },
    {
        name: 'count_consecutive',
        description: 'Count consecutive bars where condition is true',
        params: [{ name: 'condition', type: 'series<bool>' }],
        returnType: 'series<float>',
        example: 'count_consecutive(close > open)',
    },
    {
        name: 'bars_since',
        description: 'Count bars since last time condition was true',
        params: [{ name: 'condition', type: 'series<bool>' }],
        returnType: 'series<float>',
        example: 'bars_since(close > high[1])',
    },
    {
        name: 'high_since',
        description: 'Maximum high since last time condition was true',
        params: [{ name: 'condition', type: 'series<bool>' }],
        returnType: 'series<float>',
        example: 'high_since(close < open)',
    },
    {
        name: 'low_since',
        description: 'Minimum low since last time condition was true',
        params: [{ name: 'condition', type: 'series<bool>' }],
        returnType: 'series<float>',
        example: 'low_since(close > open)',
    },
    {
        name: 'max_since',
        description: 'Maximum value of series since last time condition was true',
        params: [
            { name: 'condition', type: 'series<bool>' },
            { name: 'series', type: 'series<float>' },
        ],
        returnType: 'series<float>',
        example: 'max_since(close < open, high)',
    },
    {
        name: 'min_since',
        description: 'Minimum value of series since last time condition was true',
        params: [
            { name: 'condition', type: 'series<bool>' },
            { name: 'series', type: 'series<float>' },
        ],
        returnType: 'series<float>',
        example: 'min_since(close > open, low)',
    },
    {
        name: 'accumulate_while',
        description: 'Accumulate value while condition is true, reset when false',
        params: [
            { name: 'condition', type: 'series<bool>' },
            { name: 'value', type: 'series<float>' },
        ],
        returnType: 'series<float>',
        example: 'accumulate_while(close > open, volume)',
    },
    {
        name: 'carry_while',
        description: 'First value when condition became true, held during segment; na when false',
        params: [
            { name: 'condition', type: 'series<bool>' },
            { name: 'value', type: 'series<float>' },
        ],
        returnType: 'series<float>',
        example: 'dq3_open = carry_while(session == "DQ3", open)',
    },
    {
        name: 'carry_forward',
        description: 'Last value when condition was true, propagated forward when false (LOCF)',
        params: [
            { name: 'condition', type: 'series<bool>' },
            { name: 'value', type: 'series<float>' },
        ],
        returnType: 'series<float>',
        example: 'last_dq3_close = carry_forward(session == "DQ3", close)',
    },
    {
        name: 'flag_once',
        description: 'Flag that becomes true once condition is true and stays true',
        params: [{ name: 'condition', type: 'series<bool>' }],
        returnType: 'series<bool>',
        example: 'flag_once(close > high[1])',
    },
    {
        name: 'highest',
        description: 'Maximum value in a rolling window',
        params: [
            { name: 'series', type: 'series<float>', description: 'Price series (e.g., high)' },
            { name: 'period', type: 'int', description: 'Period length' },
        ],
        returnType: 'series<float>',
        example: 'highest(high, 20)',
    },
    {
        name: 'lowest',
        description: 'Minimum value in a rolling window',
        params: [
            { name: 'series', type: 'series<float>', description: 'Price series (e.g., low)' },
            { name: 'period', type: 'int', description: 'Period length' },
        ],
        returnType: 'series<float>',
        example: 'lowest(low, 20)',
    },
    {
        name: 'weekday',
        description: 'Day of week (0=Sunday, 1=Monday, ..., 6=Saturday)',
        params: [
            { name: 'date', type: 'string', description: 'Date column or string (e.g., date)' },
        ],
        returnType: 'series<float>',
        example: 'weekday(date)',
    },
    {
        name: 'hour',
        description: 'Extract hour from timestamp (0-23)',
        params: [
            { name: 'timestamp', type: 'int', description: 'Timestamp column or int (e.g., timestamp)' },
        ],
        returnType: 'int',
        example: 'hour(timestamp)',
    },
    {
        name: 'month',
        description: 'Extract month from date or timestamp (1-12)',
        params: [
            { name: 'date_or_timestamp', type: 'string', description: 'Date column (string) or timestamp (int)' },
        ],
        returnType: 'series<float>',
        example: 'month(date)',
    },
    {
        name: 'day_of_month',
        description: 'Extract day of month from date or timestamp (1-31)',
        params: [
            { name: 'date_or_timestamp', type: 'string', description: 'Date column (string) or timestamp (int)' },
        ],
        returnType: 'series<float>',
        example: 'day_of_month(date)',
    },
    {
        name: 'year',
        description: 'Extract year from date or timestamp',
        params: [
            { name: 'date_or_timestamp', type: 'string', description: 'Date column (string) or timestamp (int)' },
        ],
        returnType: 'series<float>',
        example: 'year(date)',
    },
    {
        name: 'minute',
        description: 'Extract minute from timestamp (0-59)',
        params: [
            { name: 'timestamp', type: 'int', description: 'Timestamp column or int (e.g., timestamp)' },
        ],
        returnType: 'int',
        example: 'minute(timestamp)',
    },
];
// Built-in columns
export const BUILTIN_COLUMNS = [
    {
        name: 'high',
        type: 'series<float>',
        description: 'High price of the bar',
    },
    {
        name: 'low',
        type: 'series<float>',
        description: 'Low price of the bar',
    },
    {
        name: 'open',
        type: 'series<float>',
        description: 'Open price of the bar',
    },
    {
        name: 'close',
        type: 'series<float>',
        description: 'Close price of the bar',
    },
    {
        name: 'volume',
        type: 'series<float>',
        description: 'Volume of the bar',
    },
    {
        name: 'timestamp',
        type: 'series<int>',
        description: 'Timestamp of the bar',
    },
    {
        name: 'session',
        type: 'string',
        description: 'Session identifier (e.g., "premarket", "regular", "afterhours")',
    },
    {
        name: 'date',
        type: 'string',
        description: 'Date as string (e.g., "2025-01-15")',
    },
    {
        name: 'interval_id',
        type: 'string',
        description: 'Interval identifier',
    },
    {
        name: 'interval_start',
        type: 'series<int>',
        description: 'Interval start timestamp',
    },
    {
        name: 'interval_index',
        type: 'series<int>',
        description: 'Interval index number',
    },
];
// Keywords
export const KEYWORDS = [
    'input',
    'func',
    'signal',
    'output',
    'stats',
    'by',
    'count',
    'percent',
    'avg',
    'max',
    'min',
    'stddev',
    'median',
    'if',
    'then',
    'else',
    'as',
    'int',
    'float',
    'bool',
    'string',
    'true',
    'false',
];
// Snippets
export const SNIPPETS = [
    {
        name: 'Moving Average Crossover',
        description: 'Simple moving average crossover strategy',
        body: [
            'ma_fast = sma(close, 12)',
            'ma_slow = sma(close, 26)',
            'signal entry = crossover(ma_fast, ma_slow)',
            'signal exit = crossunder(ma_fast, ma_slow)',
            'output signals',
        ],
    },
    {
        name: 'Breakout Analysis',
        description: 'Breakout analysis with aggregations',
        body: [
            'input threshold = 0.5',
            '',
            'breakout_up = high > high[1] * (1 + threshold)',
            'breakout_down = low < low[1] * (1 - threshold)',
            '',
            'stats by session:',
            '  count(breakout_up) as total_breakouts',
            '  percent(breakout_up) as breakout_probability',
            '',
            'output stats',
        ],
    },
    {
        name: 'Basic Script',
        description: 'Basic script template',
        body: [
            '# Your analysis code here',
            'ma = sma(close, 20)',
            'output table',
        ],
    },
    {
        name: 'Stats by Weekday',
        description: 'Analysis grouped by session and weekday',
        body: [
            'breakout = high > high[1] * 1.005',
            '',
            'stats by session, weekday(date):',
            '  count(breakout) as total_breakouts',
            '  percent(breakout) as breakout_probability',
            '',
            'output stats',
        ],
    },
];
